import { useMemo } from 'react';
import type { Dicom } from '@/models/dicom';
import type { SpectralDopplerAvailability } from '@/models/spectralDoppler';
import type { SelectedImageTools } from '@/state/slices/measurementSession';
import { resolveSpectralRegion } from './calibrationRegion';

/**
 * Returns whether the given DICOM has a usable spectral Doppler region
 * for measurement. Reason is set so the UI can render a specific tooltip
 * (e.g. "No spectral Doppler region" vs "Y-axis units are Hz").
 */
export function spectralDopplerAvailabilityOf(
  dicom: Dicom | null | undefined
): SpectralDopplerAvailability {
  if (!dicom) {
    return { available: false, reason: 'no-dicom', spectralRegion: false };
  }
  const { region, reason } = resolveSpectralRegion(dicom.ultrasoundRegions);
  const spectralRegion = reason !== 'no-region';
  if (region) {
    return { available: true, reason: null, spectralRegion };
  }
  if (reason === 'hz-units') {
    return { available: false, reason: 'hz-units', spectralRegion };
  }
  return { available: false, reason: 'no-region', spectralRegion };
}

export function useSpectralDopplerAvailability(
  dicom: Dicom | null | undefined
): SpectralDopplerAvailability {
  return useMemo(() => spectralDopplerAvailabilityOf(dicom), [dicom]);
}

export function spectralDopplerUnavailableMessage(
  reason: SpectralDopplerAvailability['reason']
): string {
  switch (reason) {
    case 'hz-units':
      return 'Y-axis is in Hz — spectral Doppler requires cm/s calibration.';
    case 'no-region':
      return 'No spectral Doppler region calibrated on this image.';
    case 'no-dicom':
      return 'No image selected.';
    default:
      return 'Spectral Doppler tools available.';
  }
}

/** The tool families an image takes; null when no image is selected. */
export function imageToolsOf(
  availability: SpectralDopplerAvailability | undefined
): SelectedImageTools | null {
  if (!availability || availability.reason === 'no-dicom') return null;
  return {
    spectral: availability.available,
    twoD: !availability.spectralRegion,
  };
}
